import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, BarChart, Activity, TrendingUp, CreditCard, FileText, Users, AlertTriangle, Plus, Upload, Image } from 'lucide-react';
import { useDocuments } from '../context/DocumentsContext';
import { useClients } from '../context/ClientsContext';
import { useAuth } from '../context/AuthContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, subDays, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type TimeRange = '7' | '30' | '90';

const Dashboard: React.FC = () => {
  const { documents } = useDocuments();
  const { clients } = useClients();
  const { user, updateUserLogo } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('30');
  
  const totalInvoices = documents.filter(doc => doc.type === 'invoice').length;
  const totalQuotes = documents.filter(doc => doc.type === 'quote').length;
  const totalAmount = documents.reduce((sum, doc) => sum + doc.amount, 0);
  const paidAmount = documents
    .filter(doc => doc.status === 'paid')
    .reduce((sum, doc) => sum + doc.amount, 0);
  
  const recentDocuments = [...documents]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const getRevenueData = () => {
    const days = parseInt(timeRange);
    const endDate = new Date();
    const startDate = subDays(endDate, days);
    
    const dateLabels = Array.from({ length: days }, (_, i) => {
      return format(subDays(endDate, days - 1 - i), 'dd MMM', { locale: fr });
    });

    const revenueData = dateLabels.map((_, index) => {
      const currentDate = subDays(endDate, days - 1 - index);
      return documents
        .filter(doc => 
          doc.type === 'invoice' &&
          doc.status === 'paid' &&
          isWithinInterval(new Date(doc.date), {
            start: startOfDay(currentDate),
            end: endOfDay(currentDate)
          })
        )
        .reduce((sum, doc) => sum + doc.amount, 0);
    });

    return {
      labels: dateLabels,
      datasets: [
        {
          label: 'Revenus',
          data: revenueData,
          fill: true,
          backgroundColor: 'rgba(15, 82, 186, 0.1)',
          borderColor: 'rgba(15, 82, 186, 1)',
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: 'rgba(15, 82, 186, 1)',
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => `${context.parsed.y.toLocaleString()} €`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number) => `${value.toLocaleString()} €`
        }
      }
    }
  };
  
  const statsCards = [
    {
      title: 'Revenu Total',
      value: `${totalAmount.toLocaleString()} €`,
      change: '+12.5%',
      positive: true,
      icon: <LineChart className="h-6 w-6 text-primary-600 dark:text-primary-400" />,
      bg: 'bg-primary-50 dark:bg-primary-900/20',
    },
    {
      title: 'En attente',
      value: `${(totalAmount - paidAmount).toLocaleString()} €`,
      change: '-2.7%',
      positive: false,
      icon: <CreditCard className="h-6 w-6 text-secondary-600 dark:text-secondary-400" />,
      bg: 'bg-secondary-50 dark:bg-secondary-900/20',
    },
    {
      title: 'Documents',
      value: totalInvoices + totalQuotes,
      change: '+8.2%',
      positive: true,
      icon: <FileText className="h-6 w-6 text-accent-600 dark:text-accent-400" />,
      bg: 'bg-accent-50 dark:bg-accent-900/20',
    },
    {
      title: 'Clients',
      value: clients.length,
      change: '+3.1%',
      positive: true,
      icon: <Users className="h-6 w-6 text-success-500" />,
      bg: 'bg-green-50 dark:bg-green-900/20',
    },
  ];

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        updateUserLogo(base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tableau de bord</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Bienvenue ! Voici un aperçu de votre activité.
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleLogoUpload}
          />
          <Button
            variant="outline"
            leftIcon={user?.logo ? <Image className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
            onClick={() => fileInputRef.current?.click()}
          >
            {user?.logo ? 'Changer le Logo' : 'Ajouter un Logo'}
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/app/create')}
          >
            Créer un Document
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((card, index) => (
          <Card key={index} className="animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {card.title}
                </p>
                <h3 className="text-2xl font-bold mt-1">{card.value}</h3>
                <div className={`flex items-center mt-1 text-xs font-medium ${card.positive ? 'text-success-500' : 'text-error-500'}`}>
                  {card.positive ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                    <Activity className="h-3 w-3 mr-1" />
                  )}
                  <span>{card.change} vs mois dernier</span>
                </div>
              </div>
              <div className={`p-2 rounded-md ${card.bg}`}>
                {card.icon}
              </div>
            </div>
          </Card>
        ))}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <Card.Header
            title="Aperçu des Revenus"
            subtitle={`${timeRange} derniers jours`}
            action={
              <div className="flex gap-2">
                <Button 
                  variant={timeRange === '7' ? 'primary' : 'ghost'} 
                  size="sm"
                  onClick={() => setTimeRange('7')}
                >
                  7J
                </Button>
                <Button 
                  variant={timeRange === '30' ? 'primary' : 'ghost'} 
                  size="sm"
                  onClick={() => setTimeRange('30')}
                >
                  30J
                </Button>
                <Button 
                  variant={timeRange === '90' ? 'primary' : 'ghost'} 
                  size="sm"
                  onClick={() => setTimeRange('90')}
                >
                  90J
                </Button>
              </div>
            }
          />
          <Card.Content>
            <div className="h-[300px]">
              <Line data={getRevenueData()} options={chartOptions} />
            </div>
          </Card.Content>
        </Card>
        
        <Card>
          <Card.Header 
            title="Activité Récente" 
            subtitle="Dernière mise à jour aujourd'hui"
          />
          <Card.Content>
            {recentDocuments.length > 0 ? (
              <div className="space-y-4">
                {recentDocuments
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map(doc => (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-3 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                      onClick={() => navigate(`/app/documents/${doc.id}`)}
                    >
                      <div className="flex items-center">
                        <div className={`
                          p-2 rounded-md mr-3
                          ${doc.type === 'invoice' 
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                            : 'bg-secondary-50 dark:bg-secondary-900/20 text-secondary-600 dark:text-secondary-400'
                          }
                        `}>
                          <FileText className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-medium">{doc.type === 'invoice' ? 'Facture' : 'Devis'} #{doc.number}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {doc.amount.toLocaleString()} € - {new Date(doc.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <div
                          className={`
                            text-xs font-medium px-2 py-1 rounded-full
                            ${doc.status === 'paid' 
                              ? 'bg-green-50 dark:bg-green-900/20 text-success-500' 
                              : doc.status === 'sent' 
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                : doc.status === 'cancelled'
                                  ? 'bg-red-50 dark:bg-red-900/20 text-error-500'
                                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }
                          `}
                        >
                          {doc.status === 'paid' ? 'Payé' : 
                           doc.status === 'sent' ? 'Envoyé' : 
                           doc.status === 'draft' ? 'Brouillon' : 
                           'Annulé'}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <AlertTriangle className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-500 dark:text-gray-400">Aucun document récent</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="mt-3"
                  onClick={() => navigate('/app/create')}
                >
                  Créer un Document
                </Button>
              </div>
            )}
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;